/**
 * PageInfoButton - Reusable page guide component
 * Renders an "i" button that opens a dialog explaining page features.
 *
 * STYLE GUIDE: See docs/PAGEINFO_STYLEGUIDE.md
 * - Use the SAME icons as the page (React elements or { type: 'image', src: '...' })
 * - Never use generic icon strings when the page uses different icons
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HomeIcon from '@mui/icons-material/Home';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import ArticleIcon from '@mui/icons-material/Article';
import ApprovalIcon from '@mui/icons-material/Approval';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import TimerIcon from '@mui/icons-material/Timer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTheme } from '../../contexts/ThemeContext';
import { getMm } from "../../theme/modernMaterialEnterprise";

const ICON_BOX_STYLE = {
  width: 44,
  height: 44,
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const IMAGE_ICON_SIZE = 28;

const FeatureIcon = ({ icon, isDarkMode }) => {
  const baseBoxStyle = { ...ICON_BOX_STYLE, backgroundColor: getMm(isDarkMode).outlineVariant };

  // Support for image icons: { type: 'image', src: string, darkModeFilter?: string }
  if (typeof icon === 'object' && icon?.type === 'image') {
    return (
      <Box sx={{ ...baseBoxStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          component="img"
          src={icon.src}
          alt=""
          sx={{
            width: IMAGE_ICON_SIZE,
            height: IMAGE_ICON_SIZE,
            objectFit: 'contain',
            filter: isDarkMode && icon.darkModeFilter ? icon.darkModeFilter : 'none'
          }}
        />
      </Box>
    );
  }

  switch (icon) {
    case 'checkbox':
      return <Checkbox sx={{ p: 0, mt: 0.5, '&.Mui-checked': { color: '#4CAF50' } }} size="small" checked />;
    case 'image':
      return (
        <Box sx={{ ...baseBoxStyle, borderRadius: '50%' }}>
          <Typography sx={{ fontSize: '1.25rem' }}>👤</Typography>
        </Box>
      );
    case 'green':
      return (
        <Box sx={{ ...ICON_BOX_STYLE, backgroundColor: '#4CAF50', fontSize: '1.5rem' }}>😊</Box>
      );
    case 'red':
      return (
        <Box sx={{ ...ICON_BOX_STYLE, backgroundColor: '#EF4444', fontSize: '1.5rem' }}>😞</Box>
      );
    case 'promotion':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <MilitaryTechIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'send':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <SendIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'edit':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <EditIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'groups':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <GroupIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'settings':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <SettingsIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'pdf':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <PictureAsPdfIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'delete':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#EF4444' }}>
          <DeleteIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'search':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <SearchIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'calendar':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <CalendarTodayIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'home':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <HomeIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'qrcode':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <QrCodeIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'add':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#4CAF50' }}>
          <AddIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'people':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <PeopleIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'article':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <ArticleIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'approval':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <ApprovalIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'finance':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <AccountBalanceIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'card':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <CardMembershipIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'timer':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <TimerIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'back':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <ArrowBackIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'expand':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <ExpandMoreIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'person_add':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#374151' }}>
          <PersonAddIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    case 'remove':
      return (
        <Box sx={{ ...baseBoxStyle, backgroundColor: '#EF4444' }}>
          <RemoveIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
        </Box>
      );
    default:
      if (React.isValidElement(icon)) {
        return <Box sx={{ ...ICON_BOX_STYLE, backgroundColor: getMm(isDarkMode).outlineVariant }}>{icon}</Box>;
      }
      return null;
  }
};

/**
 * @param {Object} props
 * @param {string} props.title - Dialog title (translation key or string)
 * @param {string} props.overview - Overview text (translation key or string)
 * @param {Array<{icon: string|ReactNode, title: string, description: string}>} props.features - Feature list
 * @param {string} [props.importantNote] - Optional important note (translation key or string)
 * @param {Object} [props.sx] - Additional sx for the IconButton wrapper
 */
export default function PageInfoButton({ title, overview, features = [], importantNote, sx = {} }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);

  const resolveText = (keyOrText) => {
    if (!keyOrText) return '';
    const translated = t(keyOrText);
    return translated !== keyOrText ? translated : keyOrText;
  };

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        size="medium"
        sx={{
          color: getMm(isDarkMode).onSurfaceVariant,
          backgroundColor: getMm(isDarkMode).surfaceLowest,
          border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
          borderRadius: '8px',
          width: 44,
          height: 44,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: getMm(isDarkMode).surfaceLowest,
            borderColor: getMm(isDarkMode).outlineVariant
          },
          ...sx
        }}
        title={resolveText(title) || 'Page guide'}
      >
        <InfoIcon sx={{ fontSize: '1.25rem' }} />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            backgroundColor: getMm(isDarkMode).surfaceLowest,
            border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
            boxShadow: 'none',
            fontFamily: "'Plus Jakarta Sans', sans-serif"
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          fontSize: '1.125rem',
          color: getMm(isDarkMode).onSurface,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}>
          <InfoIcon sx={{ color: '#60A5FA' }} />
          {resolveText(title)}
        </DialogTitle>
        <DialogContent sx={{ 
          pt: 3,
          backgroundColor: getMm(isDarkMode).surfaceLowest,
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}>
          {overview && (
            <Typography sx={{ 
              fontSize: '0.875rem',
              color: getMm(isDarkMode).onSurfaceVariant,
              mb: 3,
              lineHeight: 1.6,
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              {resolveText(overview)}
            </Typography>
          )}

          {features.map((feature, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Box sx={{ mt: feature.icon === 'checkbox' ? 0.5 : 0 }}>
                <FeatureIcon icon={feature.icon} isDarkMode={isDarkMode} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: getMm(isDarkMode).onSurface, mb: 0.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {resolveText(feature.title)}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: getMm(isDarkMode).onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {resolveText(feature.description)}
                </Typography>
              </Box>
            </Box>
          ))}

          {importantNote && (
            <Box sx={{ 
              p: 2,
              mt: 2,
              borderRadius: '8px',
              backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.12)',
              border: '2px solid #F59E0B',
              boxShadow: 'none',
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: isDarkMode ? '#FCD34D' : '#92400E', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {resolveText(importantNote)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          <Button 
            onClick={() => setOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: '#374151',
              color: 'white',
              borderRadius: '8px',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              px: { xs: 2, sm: 3 },
              py: 1.5,
              fontWeight: 600,
              boxShadow: 'none',
              textTransform: 'none',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': { backgroundColor: '#374151' }
            }}
          >
            {t('close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
